<script lang="ts">
	import { _ } from 'svelte-i18n';
	import { fade, fly } from 'svelte/transition';
	import { onMount } from 'svelte';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import SeoContent from '$lib/components/sections/SeoContent.svelte';
	import Button from '$lib/components/primitives/Button.svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';

	let visible = $state(true); // SSR: start visible so hero + <h1> are in server HTML (Ahrefs: "H1 tag missing"). onMount re-set is a no-op; entrance animations still play on client-side navigation.

	onMount(() => {
		visible = true;
	});

	const personas = [
		{
			title: $_('features.timeTracking.personas.freelancers.title'),
			description: $_('features.timeTracking.personas.freelancers.description'),
			icon: 'user',
			points: [
				$_('features.timeTracking.personas.freelancers.points.0'),
				$_('features.timeTracking.personas.freelancers.points.1'),
				$_('features.timeTracking.personas.freelancers.points.2')
			]
		},
		{
			title: $_('features.timeTracking.personas.agencies.title'),
			description: $_('features.timeTracking.personas.agencies.description'),
			icon: 'users',
			points: [
				$_('features.timeTracking.personas.agencies.points.0'),
				$_('features.timeTracking.personas.agencies.points.1'),
				$_('features.timeTracking.personas.agencies.points.2')
			]
		},
		{
			title: $_('features.timeTracking.personas.consultants.title'),
			description: $_('features.timeTracking.personas.consultants.description'),
			icon: 'briefcase',
			points: [
				$_('features.timeTracking.personas.consultants.points.0'),
				$_('features.timeTracking.personas.consultants.points.1'),
				$_('features.timeTracking.personas.consultants.points.2')
			]
		}
	];

	const painPoints = [
		{
			problem: $_('features.timeTracking.painPoints.0.problem'),
			solution: $_('features.timeTracking.painPoints.0.solution')
		},
		{
			problem: $_('features.timeTracking.painPoints.1.problem'),
			solution: $_('features.timeTracking.painPoints.1.solution')
		},
		{
			problem: $_('features.timeTracking.painPoints.2.problem'),
			solution: $_('features.timeTracking.painPoints.2.solution')
		}
	];
</script>

<svelte:head>
	<title>{$_('features.timeTracking.meta.title')}</title>
	<meta name="description" content={$_('features.timeTracking.meta.description')} />
	<meta property="og:title" content="{$_('features.timeTracking.meta.title')}" />
	<meta property="og:description" content={$_('features.timeTracking.meta.description')} />
</svelte:head>

<Header />

<div class="feature-page">
	<!-- Hero Section - Story-driven opening -->
	<section class="hero">
		<div class="container">
			{#if visible}
				<div class="hero__content" in:fade={{ duration: 600, delay: 100 }}>
					<div class="hero__story" in:fly={{ y: 30, duration: 600, delay: 200 }}>
						<p class="hero__lead">{$_('features.timeTracking.hero.lead')}</p>
					</div>
					<h1 class="hero__title" in:fly={{ y: 30, duration: 600, delay: 300 }}>
						{@html $_('features.timeTracking.hero.title')}
					</h1>
					<p class="hero__subtitle" in:fly={{ y: 30, duration: 600, delay: 400 }}>
						{$_('features.timeTracking.hero.subtitle')}
					</p>
					<div class="hero__actions" in:fly={{ y: 30, duration: 600, delay: 500 }}>
						<Button variant="primary" size="lg" href="https://app.heyweek.com/?signup">
							{$_('features.timeTracking.hero.primaryButton')}
						</Button>
						<Button variant="ghost" size="lg" href="#why">
							{$_('features.timeTracking.hero.secondaryButton')}
						</Button>
					</div>
				</div>
			{/if}
		</div>
	</section>

	<!-- Problem/Solution Section -->
	<section class="problems" id="why">
		<div class="container">
			<div class="problems__header">
				<h2 class="problems__title">{$_('features.timeTracking.problems.title')}</h2>
			</div>
			<div class="problems__list">
				{#each painPoints as point}
					<div class="pain-point">
						<div class="pain-point__problem">
							<Icon name="x-circle" size={20} style="color: var(--text-tertiary);" />
							<p>{point.problem}</p>
						</div>
						<div class="pain-point__solution">
							<Icon name="check-circle" size={20} style="color: rgb(255, 94, 126);" />
							<p>{point.solution}</p>
						</div>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Who It's For Section -->
	<section class="personas">
		<div class="container">
			<div class="personas__header">
				<h2 class="personas__title">{$_('features.timeTracking.personas.title')}</h2>
			</div>
			<div class="personas__grid">
				{#each personas as persona}
					<div class="persona-card">
						<div class="persona-card__header">
							<Icon name={persona.icon} size={24} />
							<h3 class="persona-card__title">{persona.title}</h3>
						</div>
						<p class="persona-card__description">{persona.description}</p>
						<ul class="persona-card__points">
							{#each persona.points as point}
								<li>{point}</li>
							{/each}
						</ul>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Feature Showcase - Different approach -->
	<section class="showcase">
		<div class="container">
			<div class="showcase__item">
				<div class="showcase__visual">
					<div class="timer-demo">
						<div class="timer-demo__display">
							{$_('features.timeTracking.showcase.oneClick.demoTime')}
						</div>
						<div class="timer-demo__project">
							{$_('features.timeTracking.showcase.oneClick.demoProject')}
						</div>
					</div>
				</div>
				<div class="showcase__content">
					<h3 class="showcase__title">{$_('features.timeTracking.showcase.oneClick.title')}</h3>
					<p class="showcase__description">
						{$_('features.timeTracking.showcase.oneClick.description')}
					</p>
				</div>
			</div>

			<div class="showcase__item showcase__item--reverse">
				<div class="showcase__visual">
					<div class="report-demo">
						<div class="report-demo__header">
							{$_('features.timeTracking.showcase.timesheets.demoHeader')}
						</div>
						<div class="report-demo__bars">
							<div class="bar" style="height: 80%"></div>
							<div class="bar" style="height: 100%"></div>
							<div class="bar" style="height: 60%"></div>
							<div class="bar" style="height: 90%"></div>
							<div class="bar" style="height: 70%"></div>
						</div>
					</div>
				</div>
				<div class="showcase__content">
					<h3 class="showcase__title">{$_('features.timeTracking.showcase.timesheets.title')}</h3>
					<p class="showcase__description">
						{$_('features.timeTracking.showcase.timesheets.description')}
					</p>
				</div>
			</div>

			<div class="showcase__item">
				<div class="showcase__visual">
					<div class="reminder-demo">
						<Icon name="bell" size={24} style="color: rgb(255, 94, 126);" />
						<p>{$_('features.timeTracking.showcase.reminders.demoText')}</p>
					</div>
				</div>
				<div class="showcase__content">
					<h3 class="showcase__title">{$_('features.timeTracking.showcase.reminders.title')}</h3>
					<p class="showcase__description">
						{$_('features.timeTracking.showcase.reminders.description')}
					</p>
				</div>
			</div>
		</div>
	</section>

	<!-- Testimonial/Quote Section -->
	<section class="quote">
		<div class="container">
			<blockquote class="quote__content">
				<p class="quote__text">
					{$_('features.timeTracking.testimonial.text')}
				</p>
				<footer class="quote__footer">
					<span class="quote__author">{$_('features.timeTracking.testimonial.author')}</span>
				</footer>
			</blockquote>
		</div>
	</section>

	<!-- CTA Section -->
	<section class="cta">
		<div class="container">
			<div class="cta__content">
				<h2 class="cta__title">{$_('features.timeTracking.cta.title')}</h2>
				<p class="cta__subtitle">
					{$_('features.timeTracking.cta.subtitle')}
				</p>
				<div class="cta__actions">
					<Button variant="primary" size="lg" href="https://app.heyweek.com/?signup">
						{$_('features.timeTracking.cta.primaryButton')}
					</Button>
					<Button variant="ghost" size="lg" href="/pricing">
						{$_('features.timeTracking.cta.secondaryButton')}
					</Button>
				</div>
			</div>
		</div>
	</section>
</div>

<SeoContent slug="time-tracking" />

<Footer />

<style>
	.feature-page {
		min-height: 100vh;
		padding-top: var(--space-8);
	}

	/* Hero Section - Story-driven */
	.hero {
		padding: var(--space-20) 0 var(--space-16);
		background: linear-gradient(135deg, var(--bg-secondary) 0%, transparent 100%);
	}

	.hero__content {
		max-width: 900px;
		margin: 0 auto;
	}

	.hero__story {
		margin-bottom: var(--space-6);
	}

	.hero__lead {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		font-weight: var(--font-weight-medium);
		letter-spacing: -0.01em;
	}

	.hero__title {
		font-size: var(--font-size-5xl);
		font-weight: var(--font-weight-bold);
		line-height: var(--line-height-tight);
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		letter-spacing: -0.02em;
	}

	.hero__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
		max-width: 700px;
		margin: 0 auto var(--space-8);
	}

	.hero__actions {
		display: flex;
		gap: var(--space-4);
		justify-content: center;
		flex-wrap: wrap;
	}

	/* Problem/Solution Section */
	.problems {
		padding: var(--space-16) 0;
		background: var(--bg-primary);
	}

	.problems__header {
		text-align: center;
		margin-bottom: var(--space-12);
	}

	.problems__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
	}

	.problems__list {
		max-width: 800px;
		margin: 0 auto;
		display: flex;
		flex-direction: column;
		gap: var(--space-8);
	}

	.pain-point {
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
	}

	.pain-point__problem {
		display: flex;
		align-items: flex-start;
		gap: var(--space-3);
		padding: var(--space-4);
		background: var(--bg-secondary);
		border-radius: var(--radius-md);
	}

	.pain-point__problem p {
		font-size: var(--font-size-lg);
		color: var(--text-tertiary);
		font-style: italic;
	}

	.pain-point__solution {
		display: flex;
		align-items: flex-start;
		gap: var(--space-3);
		padding: var(--space-4);
		background: rgba(255, 94, 126, 0.05);
		border-radius: var(--radius-md);
		border: 1px solid rgba(255, 94, 126, 0.2);
	}

	.pain-point__solution p {
		font-size: var(--font-size-base);
		color: var(--text-primary);
		line-height: 1.6;
	}

	/* Personas Section */
	.personas {
		padding: var(--space-20) 0;
		background: var(--bg-secondary);
	}

	.personas__header {
		text-align: center;
		margin-bottom: var(--space-12);
	}

	.personas__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
	}

	.personas__grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
		gap: var(--space-6);
		max-width: 1200px;
		margin: 0 auto;
	}

	.persona-card {
		padding: var(--space-6);
		background: var(--bg-primary);
		border-radius: var(--radius-lg);
		border: 1px solid var(--border-light);
	}

	.persona-card__header {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		margin-bottom: var(--space-3);
		color: rgb(255, 94, 126);
	}

	.persona-card__title {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
	}

	.persona-card__description {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		margin-bottom: var(--space-4);
		font-weight: var(--font-weight-medium);
	}

	.persona-card__points {
		list-style: none;
		padding: 0;
		margin: 0;
	}

	.persona-card__points li {
		font-size: var(--font-size-sm);
		color: var(--text-primary);
		padding: var(--space-2) 0;
		padding-left: var(--space-6);
		position: relative;
	}

	.persona-card__points li::before {
		content: '→';
		position: absolute;
		left: 0;
		color: rgb(255, 94, 126);
	}

	/* Showcase Section */
	.showcase {
		padding: var(--space-20) 0;
	}

	.showcase__item {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-12);
		align-items: center;
		max-width: 1100px;
		margin: 0 auto var(--space-16);
	}

	.showcase__item--reverse {
		direction: rtl;
	}

	.showcase__item--reverse .showcase__content {
		direction: ltr;
	}

	.showcase__visual {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.showcase__content {
		max-width: 500px;
	}

	.showcase__title {
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.showcase__description {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: 1.6;
	}

	/* Demo Elements */
	.timer-demo {
		padding: var(--space-8);
		background: var(--bg-primary);
		border: 2px solid var(--border-light);
		border-radius: var(--radius-lg);
		box-shadow: var(--shadow-lg);
		text-align: center;
	}

	.timer-demo__display {
		font-size: 3rem;
		font-weight: var(--font-weight-bold);
		color: rgb(255, 94, 126);
		font-variant-numeric: tabular-nums;
		margin-bottom: var(--space-2);
	}

	.timer-demo__project {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
	}

	.report-demo {
		padding: var(--space-6);
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		box-shadow: var(--shadow-md);
	}

	.report-demo__header {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		margin-bottom: var(--space-4);
		text-align: center;
	}

	.report-demo__bars {
		display: flex;
		gap: var(--space-2);
		align-items: flex-end;
		height: 100px;
	}

	.bar {
		flex: 1;
		background: linear-gradient(to top, rgb(255, 94, 126), rgba(255, 94, 126, 0.6));
		border-radius: var(--radius-sm) var(--radius-sm) 0 0;
	}

	.reminder-demo {
		display: flex;
		align-items: center;
		gap: var(--space-4);
		padding: var(--space-4) var(--space-6);
		background: var(--bg-primary);
		border: 1px solid rgb(255, 94, 126);
		border-radius: var(--radius-md);
		box-shadow: var(--shadow-lg);
	}

	.reminder-demo p {
		font-size: var(--font-size-base);
		color: var(--text-primary);
	}

	/* Quote Section */
	.quote {
		padding: var(--space-16) 0;
		background: linear-gradient(135deg, rgba(255, 94, 126, 0.05), transparent);
	}

	.quote__content {
		max-width: 800px;
		margin: 0 auto;
		text-align: center;
	}

	.quote__text {
		font-size: var(--font-size-2xl);
		color: var(--text-primary);
		font-weight: var(--font-weight-medium);
		line-height: 1.4;
		margin-bottom: var(--space-6);
	}

	.quote__footer {
		font-size: var(--font-size-base);
	}

	.quote__author {
		color: var(--text-secondary);
		font-style: italic;
	}

	/* CTA Section */
	.cta {
		padding: var(--space-20) 0;
		text-align: center;
		background: linear-gradient(180deg, transparent 0%, var(--bg-secondary) 100%);
	}

	.cta__content {
		max-width: 700px;
		margin: 0 auto;
	}

	.cta__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.cta__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		margin-bottom: var(--space-8);
		line-height: 1.6;
	}

	.cta__actions {
		display: flex;
		gap: var(--space-4);
		justify-content: center;
		flex-wrap: wrap;
	}

	/* Responsive */
	@media (max-width: 768px) {
		.hero__title {
			font-size: 2rem;
		}

		.personas__grid {
			grid-template-columns: 1fr;
		}

		.showcase__item,
		.showcase__item--reverse {
			grid-template-columns: 1fr;
			gap: var(--space-6);
		}

		.showcase__item--reverse {
			direction: ltr;
		}

		.quote__text {
			font-size: var(--font-size-xl);
		}
	}
</style>
